// shared/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import _ from 'lodash';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { SearchComponentProps } from './types';

type SetStateAction<S> = Partial<S> | ((prevState: S) => Partial<S> | null);

/**
 * Provides class-style partial updates for a single state object. Wraps useState with a memoised setter that merges a patch object, or the result of a patch function, into the previous state through lodash assign and keeps the previous state when a patch function returns nothing.
 *
 * @param initialState - Initial value of the state object.
 * @returns Tuple of the current state and the merging setter.
 */
const useSetState = <S extends object>(
  initialState: S,
): [S, (patch: SetStateAction<S>) => void] => {
  const [state, set] = useState<S>(initialState);
  /**
   * Merges a partial update into the state object, class-component style.
   * Applies a patch function to the previous state and keeps that state when it returns nothing,
   * otherwise assigns the patch or its result over a copy of the previous state.
   *
   * @param patch - Partial state, or a function deriving one from the previous state.
   * @returns Nothing.
   */
  const setState = useCallback((patch: SetStateAction<S>) => {
    set((prevState) => {
      if (_.isFunction(patch)) {
        const data = patch(prevState);
        if (_.isNil(data)) {
          return prevState;
        }
        return _.assign({}, prevState, data);
      }
      return _.assign({}, prevState, patch);
    });
  }, []);
  return [state, setState];
};

/**
 * Runs an effect only when its dependencies change, skipping the first render.
 * Tracks the initial mount in a ref, calls the effect on every later dependency change and returns
 * the given cleanup from each run.
 *
 * @param effect - Callback run after a dependency change.
 * @param dependencies - Values whose change re-runs the effect.
 * @param cleanup - Cleanup returned from the effect on each run.
 * @returns Nothing.
 */
function useUpdateEffect(effect: (...args: any[]) => any, dependencies: any[] = [], cleanup?: any) {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      effect();
    }
    return cleanup;
  }, dependencies);
}

/**
 * Drives the shared search bar, debouncing typed text before it reaches the parent filter. Keeps searchValue in local state, collapses whitespace on typing, forwards search-by field and option selections immediately, exposes resetState through the imperative ref, mirrors external searchValue changes, and pushes the text to props.onChange 500ms after the last keystroke.
 *
 * @param props - The search component props, carrying searchValue, searchByValue and the parent onChange callback.
 * @param ref - Forwarded ref exposing resetState to the parent.
 * @returns The local search state plus the select, option-select and text change handlers.
 */
export const useSearchComponent = (props: SearchComponentProps, ref: any) => {
  const updateSearchValueTimeout = useRef<NodeJS.Timeout | null>(null);

  const [state, setState] = useSetState({
    searchValue: props.searchValue || null,
  });

  /**
   * Reports a newly picked search-by field to the parent under the searchByValue key. Skips the call when the picked value already equals props.searchByValue.
   *
   * @param _name - The field name, unused.
   * @param value - The newly selected search-by field.
   * @returns Nothing.
   */
  const onChangeSelect = (_name: any, value: any) => {
    if (value !== props.searchByValue) {
      props.onChange('searchByValue', value);
    }
  };

  /**
   * Forwards a picked search-value option to the parent as the whole option object. Calls props.onChange with the field name and the option rather than the bare value so the option metadata survives.
   *
   * @param _name - The field name forwarded to the parent.
   * @param value - The selected value, unused.
   * @param option - The full selected option object.
   * @returns Nothing.
   */
  const onChangeSearchSelect = (_name: any, value: any, option: any) => {
    props.onChange(_name, option);
  };

  /**
   * Stores typed search text after collapsing whitespace into plain spaces. Replaces space, whitespace and non-breaking-space sequences then writes searchValue to local state, which the debounced effect later forwards to the parent.
   *
   * @param _name - The field name, unused.
   * @param value - The raw entered search text.
   * @returns Nothing.
   */
  const onChangeSearch = (_name: any, value: any) => {
    const searchValue = value?.replace(/ |\s|&nbsp;/g, ' ');
    setState({ searchValue });
  };

  useImperativeHandle(ref, () => ({
    resetState: () => {
      setState({
        searchValue: null,
      });
    },
  }));

  useUpdateEffect(() => {
    if (props.searchValue !== state.searchValue) {
      setState({ searchValue: props.searchValue });
    }
  }, [props.searchValue]);

  useUpdateEffect(
    () => {
      if (updateSearchValueTimeout.current) {
        clearTimeout(updateSearchValueTimeout.current);
        updateSearchValueTimeout.current = null;
      }

      updateSearchValueTimeout.current = setTimeout(() => {
        props.onChange('searchValue', state.searchValue);
      }, 500);
    },
    [state.searchValue],
    () => {
      if (updateSearchValueTimeout.current) {
        clearTimeout(updateSearchValueTimeout.current);
        updateSearchValueTimeout.current = null;
      }
    },
  );

  return {
    state,
    onChangeSelect,
    onChangeSearchSelect,
    onChangeSearch,
  };
};
